/**
 * /me/pay — Me → Pay → Payslips (Phase 1a, employee self-service): the
 * salary slips, compensation revision letters and released Form 16.
 *
 * The salary breakup lives on its own tab (/me/salary) and the tax
 * declaration on Me → Pay → Tax (/me/tax), so the slips are the first
 * thing on the page (wave-3 review: on a phone they sat ~20 rows down,
 * under the breakup table).
 *
 * Today a plain employee cannot reach their own payslip in the UI:
 * payroll is HR-admin-gated in the sidebar and tax declaration is
 * buried in the avatar dropdown. This surface gives EVERY role a
 * personal home for their salary slips + a link to the income-tax
 * declaration.
 *
 * Backend contract (built in parallel — see lib/api/payroll.ts):
 *   GET /api/v1/payroll/me/payslips  (any authed user) →
 *     { payslips: [{ run_id, period_year, period_month,
 *       net_paise, locked_at }] }  — the caller's OWN slips from
 *     LOCKED runs, newest first. The server derives the employee from
 *     the session; no id is sent.
 *
 * Slip download mirrors the HR run-detail "Download Slip" button: an
 * authed blob fetch via the shared axios client, saved with saveBlobAs
 * under the server's Content-Disposition filename
 * (salary-slip-YYYY-MM-<empcode>.pdf — read from the response, because a
 * blob: URL carries no headers; audit Q27: the link without a download
 * name navigated the app — or the installed PWA — to the PDF). The
 * employee_id in the existing slip endpoint is the CURRENT USER's own id
 * (useAuth().user.user_id), which is exactly what the backend authz
 * allows (HR-admin OR self). The fallback name carries neither an
 * internal id nor a display name.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Download, Loader2, ReceiptIndianRupee } from "lucide-react";
import { toast } from "sonner";
import { payrollApi, toApiError } from "@/lib/api";
import { saveBlobAs } from "@/lib/save-blob";
import { useMyPayslips } from "@/hooks/use-payroll";
import { useAuth } from "@/lib/auth/auth-context";
import { formatPaise } from "@/lib/expense-format";
import { formatPeriod, type MyPayslip } from "@/types/payroll";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import { MyCompRevisionLetters } from "@/components/payroll/comp-revision-letters";
import { MyForm16Card } from "@/components/payroll/form16-release";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/pay")({
  component: MyPayRoute,
});

function PaySkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  );
}

function MyPayRoute() {
  const { user } = useAuth();
  const employeeId = user?.user_id;
  const q = useMyPayslips();

  // Track the in-flight download by run_id so only that row's button
  // shows a spinner / disables.
  const [downloadingRunId, setDownloadingRunId] = useState<number | null>(null);

  async function downloadSlip(p: MyPayslip) {
    if (!employeeId) {
      toast.error("Could not determine your employee account. Please re-login.");
      return;
    }
    setDownloadingRunId(p.run_id);
    try {
      const { blob, filename } = await payrollApi.downloadSalarySlipFile(p.run_id, employeeId);
      saveBlobAs(
        blob,
        filename ?? `salary-slip-${p.period_year}-${String(p.period_month).padStart(2, "0")}.pdf`,
      );
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setDownloadingRunId(null);
    }
  }

  const payslips = q.data?.payslips ?? [];

  return (
    <MeModuleShell
      title="Payslips"
      description="Your salary slips, revision letters and Form 16."
    >
      <div className="space-y-6">
        {/* Salary slips */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ReceiptIndianRupee aria-hidden className="h-5 w-5 text-primary" />
              Salary Slips
            </CardTitle>
            <CardDescription>
              Monthly slips from finalised payroll runs (Payment of Wages
              Act 1936 §13A). Newest first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {q.isLoading && <PaySkeleton />}

            {q.isError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load your payslips</AlertTitle>
                <AlertDescription>{toApiError(q.error).message}</AlertDescription>
              </Alert>
            )}

            {!q.isLoading && !q.isError && payslips.length === 0 && (
              <div className="rounded-md border border-dashed p-8 text-center">
                <ReceiptIndianRupee
                  aria-hidden
                  className="mx-auto h-8 w-8 text-muted-foreground"
                />
                <p className="mt-3 text-sm font-medium text-gray-900">
                  No payslips yet
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Your salary slips will appear here once payroll is finalised.
                </p>
              </div>
            )}

            {!q.isLoading && !q.isError && payslips.length > 0 && (
              // A list, not a table: at phone width (390 px) the table's
              // third column clipped the Download button (persona round
              // 2026-10). Each row wraps; the button goes full-width on
              // a phone.
              <ul className="divide-y rounded-md border" data-testid="my-pay-slips">
                {payslips.map((p) => {
                  const busy = downloadingRunId === p.run_id;
                  return (
                    <li
                      key={p.run_id}
                      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-3 sm:px-4"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900">
                          {formatPeriod(p.period_year, p.period_month)}
                          {p.run_type === "supplementary" && (
                            <span
                              className="ml-2 rounded border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-800"
                              title="A correction to this month's pay — paid in addition to the original payslip"
                            >
                              Correction
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Net pay{" "}
                          <span className="tabular-nums text-gray-900">
                            {formatPaise(p.net_paise)}
                          </span>
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-1.5 sm:w-auto"
                        disabled={busy || !employeeId}
                        onClick={() => downloadSlip(p)}
                        data-testid={`my-pay-row-${p.run_id}-download-slip`}
                      >
                        {busy ? (
                          <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download aria-hidden className="h-4 w-4" />
                        )}
                        Download Slip
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Compensation revision letters HR issued to you (download +
            acknowledge). Renders nothing when there are none. */}
        <MyCompRevisionLetters employeeId={employeeId} />

        {/* Form 16 HR has released (wave B). Renders nothing when none. */}
        <MyForm16Card />
      </div>
    </MeModuleShell>
  );
}
